<script setup lang="ts">
import { computed } from 'vue'

import { RepositoryTimeline } from '@types'

import { cn } from '@ds/lib/utils'
import { buildAxisPeriods } from './utils/helpers'

const PADDING_LEFT = 16

type Props = {
  /** Oldest-first period end dates — the whole range is drawn on the axis at once, nothing scrolls. */
  dates: string[]
  freq: RepositoryTimeline['freq']
  selected?: string
  disabled?: boolean
  /** Ticks only, no month labels — the phone-width layout from the design mock, where labels drop off the axis. */
  dense?: boolean
}

type Emits = {
  select: [date: string]
}

const { dates, freq, selected, disabled = false, dense = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScrollerAxis',
})

const periods = computed(() => buildAxisPeriods(dates, freq))

const currentDateIndex = computed(() => periods.value.findIndex(period => period.date === selected))

const periodWidth = computed(() => 100 / periods.value.length)
const periodStyle = computed(() => ({
  width: currentDateIndex.value > 0 ? `${periodWidth.value}%` : `calc(${periodWidth.value / 2}% + ${PADDING_LEFT}px)`,
  left: currentDateIndex.value > 0 ? `${(currentDateIndex.value * periodWidth.value)}%` : `${-PADDING_LEFT}px`,
  transform: currentDateIndex.value > 0 ? 'translateX(-50%)' : undefined,
  backgroundImage: `repeating-linear-gradient(  
    -45deg,
    rgb(var(--ds-primary)) 0,
    rgb(var(--ds-primary)) 5px,
    rgb(var(--ds-background)) 5px,
    rgb(var(--ds-background)) 10px
  )`,
}))

const tickClass = (isSelected: boolean) => cn(
  'block transition-[height,border-color] duration-ds-fast',
  dense
    ? isSelected
      ? 'mt-0 h-3.5 border-l-[3px] border-primary'
      : 'mt-0.75 h-2 border-l border-strong'
    : isSelected
      ? 'mt-0.5 h-3.75 border-l-2 border-primary'
      : 'mt-0.5 h-2 border-l border-strong',
)
</script>

<template>
  <div
    class="relative"
    :class="dense ? 'h-7.5' : 'h-14'"
  >
    <div
      class="absolute inset-x-0 h-px bg-border"
      :class="dense ? 'top-1.75' : 'top-6.75'"
    />
    <div
      class="absolute inset-x-0 h-0.75 rounded-xs bg-primary/16"
      :class="dense ? 'top-1.5' : 'top-6.5'"
    />
    <div
      class="absolute top-1/2 -bottom-1 -mt-2 sm:mt-0 transition-[width,left,transform] border-x-2 border-primary opacity-65"
      :class="currentDateIndex === 0 ? 'rounded-l-none border-l-0' : undefined"
      :style="periodStyle"
    />
    <div
      class="relative flex h-full"
    >
      <button
        v-for="(period, index) in periods"
        :key="period.date"
        type="button"
        :data-date="period.date"
        :title="period.date"
        :aria-pressed="period.date === selected"
        :disabled
        :style="{ width: `${index === 0 ? periodWidth / 2 : periodWidth}%` }"
        :class="cn(
          'relative flex min-w-0 flex-col items-end border-0 bg-transparent p-0 cursor-pointer',
          'disabled:pointer-events-none disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/16 rounded-ds-sm',
          dense ? 'h-8 -mt-1.75 -mb-1.75 gap-0.5 pt-1.75' : 'h-full gap-1',
        )"
        @click="emit('select', period.date)"
      >
        <span
          v-if="!dense"
          class="translate-x-1/2 inline-flex h-5 items-center rounded-full text-[0.71875rem] font-bold leading-none tracking-ds-tight transition-colors duration-ds-fast z-10"
          :class="period.date === selected
            ? 'px-2.25 bg-primary text-primary-foreground shadow-ds-sm'
            : 'px-0 text-foreground'"
        >{{ period.label }}</span>
        <span :class="tickClass(period.date === selected)" />
        <span
          class="translate-x-1/2 whitespace-nowrap text-[0.625rem] font-bold leading-none text-muted-foreground z-10"
          :class="dense ? 'text-[0.59375rem] tracking-[0.04em]' : 'tracking-ds-wide'"
        >{{ period.isYearStart ? (dense ? period.yearShort : period.yearFull) : '' }}</span>
      </button>
    </div>
  </div>
</template>
